import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  InputAdornment,
  Box,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const MAPBOX_URL = "https://api.mapbox.com/search/geocode/v6/forward";
const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";

// Bias Mapbox results to Sweden centroid.
const BIAS = { lat: 62.0, lng: 15.0 };

const normaliseMapbox = (feature) => {
  const p = feature.properties ?? {};
  const ctx = p.context ?? {};
  const [lng, lat] = feature.geometry?.coordinates ?? [];

  // `name` on address features is usually the full street + number already.
  const primary = p.name || ctx.address?.name || p.full_address || "Okänd plats";

  const secondaryParts = [];
  if (ctx.postcode?.name) secondaryParts.push(ctx.postcode.name);
  const place =
    ctx.place?.name ||
    ctx.locality?.name ||
    ctx.district?.name ||
    ctx.region?.name;
  if (place) secondaryParts.push(place);

  return {
    primary,
    secondary: secondaryParts.join(" · "),
    label: p.full_address || `${primary}, ${secondaryParts.join(" ")}`,
    lat: Number(lat),
    lng: Number(lng),
    raw: feature,
  };
};

const normaliseNominatim = (row) => {
  const a = row.address ?? {};
  const primaryParts = [];
  const street = a.road || a.pedestrian || a.footway || a.path;
  if (street) {
    primaryParts.push(a.house_number ? `${street} ${a.house_number}` : street);
  } else if (row.name) {
    primaryParts.push(row.name);
  } else if (row.display_name) {
    primaryParts.push(row.display_name.split(",")[0]);
  }
  const secondaryParts = [];
  if (a.postcode) secondaryParts.push(a.postcode);
  const place =
    a.city || a.town || a.village || a.municipality || a.suburb || a.county;
  if (place) secondaryParts.push(place);
  return {
    primary: primaryParts.join(", ") || row.display_name,
    secondary: secondaryParts.join(" · "),
    label: row.display_name,
    lat: Number(row.lat),
    lng: Number(row.lon),
    raw: row,
  };
};

const searchMapbox = async (q, signal) => {
  if (!MAPBOX_TOKEN) return [];
  const params = new URLSearchParams({
    q,
    access_token: MAPBOX_TOKEN,
    country: "se",
    language: "sv",
    limit: "7",
    autocomplete: "true",
    proximity: `${BIAS.lng},${BIAS.lat}`,
    types: "address,street,place,postcode,locality,neighborhood",
  });
  const res = await fetch(`${MAPBOX_URL}?${params.toString()}`, {
    signal,
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`mapbox ${res.status}`);
  const json = await res.json();
  const features = Array.isArray(json?.features) ? json.features : [];
  return features.map(normaliseMapbox);
};

const searchNominatim = async (q, signal) => {
  const params = new URLSearchParams({
    format: "json",
    q,
    countrycodes: "se",
    limit: "7",
    addressdetails: "1",
    "accept-language": "sv",
    email: "admin@varna.app",
  });
  const res = await fetch(`${NOMINATIM_URL}?${params.toString()}`, {
    signal,
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`nominatim ${res.status}`);
  const json = await res.json();
  return Array.isArray(json) ? json.map(normaliseNominatim) : [];
};

// Mapbox primary; Nominatim only if Mapbox is unavailable or empty.
const searchAddress = async (rawQuery, signal) => {
  const q = rawQuery.trim();
  if (!q) return [];

  if (MAPBOX_TOKEN) {
    try {
      const results = await searchMapbox(q, signal);
      if (results.length > 0) return results;
    } catch (err) {
      if (err.name === "AbortError") throw err;
      console.warn("Mapbox search failed, falling back:", err);
    }
  }
  try {
    return await searchNominatim(q, signal);
  } catch (err) {
    if (err.name === "AbortError") throw err;
    return [];
  }
};

export default function AddressSearchField({
  value,
  onSelect,
  placeholder = "Sök gata, ort eller plats…",
}) {
  const [input, setInput] = useState(value ?? "");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => {
    setInput(value ?? "");
  }, [value]);

  useEffect(() => {
    const q = input?.trim();
    if (!q || q.length < 2) {
      setOptions([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      try {
        const results = await searchAddress(q, controller.signal);
        setOptions(results);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.warn("address search failed:", err);
          setOptions([]);
        }
      } finally {
        setLoading(false);
      }
    }, 220);

    return () => clearTimeout(timer);
  }, [input]);

  const filterOptions = useMemo(() => (x) => x, []);

  return (
    <Autocomplete
      freeSolo
      fullWidth
      options={options}
      loading={loading}
      filterOptions={filterOptions}
      noOptionsText={
        input && input.length >= 2
          ? loading
            ? "Söker…"
            : "Inga träffar — pröva med enbart gatunamn eller ort"
          : "Skriv minst två tecken"
      }
      getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.label)}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      onChange={(_, opt) => {
        if (opt && typeof opt !== "string") onSelect?.(opt);
      }}
      isOptionEqualToValue={(a, b) =>
        a?.lat === b?.lat && a?.lng === b?.lng && a?.primary === b?.primary
      }
      renderOption={(props, opt) => (
        <Box
          component="li"
          {...props}
          sx={{
            display: "flex",
            alignItems: "flex-start !important",
            gap: 1.5,
            py: 1.25,
          }}
        >
          <PlaceRoundedIcon sx={{ color: "#8a1125", mt: "2px", fontSize: 18 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#10324a",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {opt.primary}
            </Typography>
            {opt.secondary ? (
              <Typography
                variant="caption"
                sx={{
                  color: "#6b7380",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                }}
              >
                {opt.secondary}
              </Typography>
            ) : null}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          variant="outlined"
          size="medium"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2.5,
              background: "#ffffff",
              fontSize: 15,
              "& fieldset": { borderColor: "#e2e6eb" },
              "&:hover fieldset": { borderColor: "#c9cfd7" },
              "&.Mui-focused fieldset": {
                borderColor: "#8a1125",
                borderWidth: 1.5,
              },
            },
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ color: "#8a1125" }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
